/**
 * D5 — Campaign disposition dashboard for voice campaigns.
 *
 * Live counters + per-call list + pause/resume/stop controls + per-call
 * retry timeline + recording playback.
 *
 * Auto-refresh every 5s while campaign.status='running'; pauses on
 * 'paused'/'completed'/'failed'. Stops auto-refresh when tab not visible.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { COLORS } from "../../../theme/colors";
import { Button, Badge, Card, Modal } from "../../../components/ui";
import { useAuth } from "../../../contexts/AuthContext";
import { campaignApi } from "../../../api/client";
import { canViewFullRecipientNumber, formatPhone } from "../../../utils/phone";

const DISPOSITION_COLORS = {
  completed: COLORS.green,
  answered_human: COLORS.green,
  voicemail: COLORS.cyan,
  answered_voicemail: COLORS.cyan,
  no_answer: COLORS.orange,
  busy: COLORS.orange,
  failed: COLORS.pink,
  rejected: COLORS.pink,
  pending: COLORS.textMuted,
};

export default function CampaignDispositionDashboard({ campaignId, onPaused, onResumed }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const intervalRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await campaignApi.dispositions(campaignId, 50);
      setData(res);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  }, [campaignId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh while campaign is actively running
  useEffect(() => {
    if (!data) return;
    const shouldRefresh = ["running", "scheduled"].includes(data.status);
    if (shouldRefresh) {
      intervalRef.current = setInterval(() => {
        if (document.visibilityState === "visible") refresh();
      }, 5000);
      return () => clearInterval(intervalRef.current);
    }
  }, [data?.status, refresh]);

  if (loading) return <div style={{ color: COLORS.textMuted }}>Loading dispositions…</div>;
  if (error) return <div style={{ color: COLORS.pink }}>{error}</div>;
  if (!data) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <CampaignControlBar campaign={data} onUpdate={refresh} onPaused={onPaused} onResumed={onResumed} />
      <SummaryCards data={data} />
      <DispositionMixChart counts={data.disposition_counts || {}} />
      <CallList calls={data.last_calls || []} onSelect={setSelectedCall} />
      {selectedCall && (
        <VoiceCallDetailModal
          campaignId={campaignId}
          call={selectedCall}
          onClose={() => setSelectedCall(null)}
        />
      )}
    </div>
  );
}


function CampaignControlBar({ campaign, onUpdate, onPaused, onResumed }) {
  const [busy, setBusy] = useState(false);
  const handle = async (action) => {
    setBusy(true);
    try {
      if (action === "pause") {
        await campaignApi.pause(campaign.campaign_id);
        if (onPaused) onPaused();
      } else if (action === "resume") {
        await campaignApi.resume(campaign.campaign_id);
        if (onResumed) onResumed();
      } else if (action === "stop") {
        if (!confirm("Stop the campaign? In-flight calls drain cleanly; remaining recipients are CANCELLED (not just deferred). Status flips to completed_with_errors. This is one-way — to dispatch again you must Reset to Draft.")) {
          setBusy(false);
          return;
        }
        // D6 — B16-shipped /stop endpoint. Unsent recipients are
        // marked cancelled with failed_reason=campaign_stopped;
        // campaign goes to completed[_with_errors] and CANNOT be
        // resumed. Pre-fix the button silently fell through to
        // pause() so "Stop" actually behaved like "Pause".
        await campaignApi.stop(campaign.campaign_id);
        if (onPaused) onPaused();
      } else if (action === "trigger") {
        await campaignApi.trigger(campaign.campaign_id);
      }
      onUpdate();
    } catch (e) {
      alert(`Action failed: ${e.message}`);
    } finally { setBusy(false); }
  };

  const statusColor =
    campaign.status === "running" ? COLORS.green :
    campaign.status === "paused" ? COLORS.orange :
    campaign.status === "completed" ? COLORS.textMuted :
    campaign.status === "failed" ? COLORS.pink :
    COLORS.cyan;

  return (
    <Card padding="14px 18px">
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Badge color={statusColor}>{campaign.status}</Badge>
        {campaign.paused_reason && (
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>
            paused: {campaign.paused_reason}
          </span>
        )}
        <div style={{ flex: 1 }} />
        {campaign.status === "running" && (
          <>
            <Button onClick={() => handle("pause")} disabled={busy} variant="ghost">Pause</Button>
            <Button onClick={() => handle("stop")} disabled={busy} variant="ghost">Stop</Button>
          </>
        )}
        {campaign.status === "paused" && (
          <>
            <Button onClick={() => handle("resume")} disabled={busy}>Resume</Button>
            <Button onClick={() => handle("trigger")} disabled={busy}>Re-trigger</Button>
          </>
        )}
        {campaign.status === "scheduled" && (
          <Button onClick={() => handle("trigger")} disabled={busy}>Dispatch</Button>
        )}
      </div>
    </Card>
  );
}


function SummaryCards({ data }) {
  const spendUsed = data.spend_usd_today || 0;
  const spendCap = data.spend_cap_usd || 0;
  const spendPct = spendCap > 0 ? (spendUsed / spendCap) * 100 : 0;
  const spendColor = spendPct > 95 ? COLORS.pink : spendPct > 80 ? COLORS.orange : COLORS.green;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
      <Card padding="14px 16px">
        <SmallMetric label="Total recipients" value={data.total_recipients} />
      </Card>
      <Card padding="14px 16px">
        <SmallMetric label="Dispatched" value={data.total_dispatched} color={COLORS.cyan} />
      </Card>
      <Card padding="14px 16px">
        <SmallMetric label="In flight" value={data.total_in_flight} color={COLORS.accent} />
      </Card>
      <Card padding="14px 16px">
        <SmallMetric
          label="Spend today"
          value={`$${spendUsed.toFixed(2)} / ${spendCap > 0 ? `$${spendCap.toFixed(2)}` : "(no cap)"}`}
          color={spendColor}
        />
      </Card>
      {data.circuit_breaker_state && (
        <Card padding="14px 16px">
          <SmallMetric
            label="Circuit breaker"
            value={data.circuit_breaker_state}
            color={data.circuit_breaker_state === "open" ? COLORS.pink : data.circuit_breaker_state === "half_open" ? COLORS.orange : COLORS.green}
          />
        </Card>
      )}
    </div>
  );
}

function SmallMetric({ label, value, color }) {
  return (
    <>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: COLORS.textMuted }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: color || COLORS.text, marginTop: 4 }}>{value}</div>
    </>
  );
}


function DispositionMixChart({ counts }) {
  const entries = Object.entries(counts).filter(([_, v]) => v > 0);
  const total = entries.reduce((s, [_, v]) => s + v, 0);
  if (total === 0) {
    return (
      <Card padding="14px 18px">
        <div style={{ fontSize: 13, color: COLORS.textMuted }}>No dispositions yet — campaign hasn't dispatched any calls.</div>
      </Card>
    );
  }
  return (
    <Card padding="14px 18px">
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Disposition mix</div>
      <div style={{ display: "flex", height: 28, borderRadius: 6, overflow: "hidden" }}>
        {entries.map(([key, count]) => (
          <div
            key={key}
            title={`${key}: ${count} (${((count / total) * 100).toFixed(1)}%)`}
            style={{
              flex: count,
              background: DISPOSITION_COLORS[key] || COLORS.textMuted,
              cursor: "default",
            }}
          />
        ))}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 10, fontSize: 12 }}>
        {entries.map(([key, count]) => {
          // D11 — display percentages alongside raw counts so the
          // operator can read mix at a glance instead of doing the
          // math from the proportional bar widths.
          const pct = ((count / total) * 100).toFixed(1);
          return (
            <span key={key} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: DISPOSITION_COLORS[key] || COLORS.textMuted, display: "inline-block" }} />
              {key}: {count} ({pct}%)
            </span>
          );
        })}
      </div>
    </Card>
  );
}


function CallList({ calls, onSelect }) {
  // Recipient numbers are masked everywhere in the dashboard; the full value is
  // audit-log-only (see the detail modal), so no role check is needed here.
  return (
    <Card padding="14px 18px">
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Recent calls (last 50)</div>
      {calls.length === 0 ? (
        <div style={{ fontSize: 13, color: COLORS.textMuted, padding: "12px 0" }}>No calls yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {calls.map(c => (
            <div
              key={c.message_id}
              onClick={() => onSelect(c)}
              style={{
                display: "grid",
                gridTemplateColumns: "1.2fr 1fr 80px 80px 80px 24px",
                gap: 12,
                alignItems: "center",
                padding: "8px 12px",
                background: COLORS.surface,
                borderRadius: 6,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              <span style={{ fontFamily: "monospace" }}>
                {c.recipient_phone_masked}
                {c.recipient_name && <span style={{ color: COLORS.textMuted, marginLeft: 8 }}>{c.recipient_name}</span>}
              </span>
              <span>
                <Badge color={DISPOSITION_COLORS[c.disposition] || COLORS.textMuted}>
                  {c.disposition || "pending"}
                </Badge>
              </span>
              <span style={{ color: COLORS.textMuted, fontSize: 12 }}>
                {c.duration_seconds ? `${c.duration_seconds}s` : "—"}
              </span>
              <span style={{ color: COLORS.textMuted, fontSize: 12 }}>
                {c.actual_cost_usd != null ? `$${c.actual_cost_usd.toFixed(3)}` : "—"}
              </span>
              <span style={{ color: COLORS.textMuted, fontSize: 11 }}>
                {c.dispatch_attempts > 1 && <Badge color={COLORS.orange}>×{c.dispatch_attempts}</Badge>}
              </span>
              <span style={{ color: COLORS.textMuted, fontSize: 14 }}>→</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


function VoiceCallDetailModal({ campaignId, call, onClose }) {
  const { currentUser } = useAuth();
  const fullVisible = canViewFullRecipientNumber(currentUser?.role);
  const [showFull, setShowFull] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [recordingError, setRecordingError] = useState(null);
  const [loadingRecording, setLoadingRecording] = useState(false);

  const loadRecording = async () => {
    setLoadingRecording(true);
    setRecordingError(null);
    try {
      const res = await campaignApi.recording(campaignId, call.message_id, 600);
      setRecordingUrl(res.presigned_url);
    } catch (e) {
      setRecordingError(e.message);
    } finally { setLoadingRecording(false); }
  };

  return (
    <Modal open onClose={onClose} title="Call detail" width={600}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Section label="Recipient">
          <div style={{ fontFamily: "monospace", fontSize: 14 }}>
            {showFull && fullVisible ? "(full number unavailable in dashboard — query Compliance → Audit log)" : call.recipient_phone_masked}
            {fullVisible && (
              <Button onClick={() => setShowFull(s => !s)} variant="link" style={{ marginLeft: 12, fontSize: 12 }}>
                {showFull ? "Hide" : "Show full (audit logged)"}
              </Button>
            )}
          </div>
          {call.recipient_name && <div style={{ color: COLORS.textMuted, marginTop: 2 }}>{call.recipient_name}</div>}
        </Section>

        <Section label="Disposition">
          <Badge color={DISPOSITION_COLORS[call.disposition] || COLORS.textMuted}>{call.disposition || "pending"}</Badge>
          {call.amd_verdict && <span style={{ marginLeft: 12, fontSize: 12, color: COLORS.textMuted }}>AMD: {call.amd_verdict}</span>}
        </Section>

        <Section label="Timeline">
          <RetryTimeline call={call} />
        </Section>

        <Section label="Recording">
          {recordingUrl ? (
            <audio controls src={recordingUrl} style={{ width: "100%" }} />
          ) : (
            <Button onClick={loadRecording} disabled={loadingRecording}>
              {loadingRecording ? "Loading…" : "Load recording (admin)"}
            </Button>
          )}
          {recordingError && <div style={{ color: COLORS.pink, fontSize: 12, marginTop: 6 }}>{recordingError}</div>}
        </Section>

        <Section label="Cost + duration">
          <div style={{ fontSize: 13 }}>
            Duration: {call.duration_seconds || "—"}s · Cost: {call.actual_cost_usd != null ? `$${call.actual_cost_usd.toFixed(4)}` : "—"}
          </div>
        </Section>
      </div>
    </Modal>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: COLORS.textMuted, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function RetryTimeline({ call }) {
  if (!call.dispatch_attempts || call.dispatch_attempts <= 1) {
    return (
      <div style={{ fontSize: 13, color: COLORS.textMuted }}>
        Single attempt — no retries.
      </div>
    );
  }
  // For v3, we don't have a per-attempt history endpoint yet — show
  // aggregate attempt count + latest disposition. v3.1 wires this to
  // voice_call_dispatch_log query.
  return (
    <div style={{ fontSize: 13 }}>
      <div>{call.dispatch_attempts} dispatch attempts</div>
      <div style={{ color: COLORS.textMuted, marginTop: 4, fontSize: 12 }}>
        Latest: {call.disposition || "pending"} {call.ended_at && `at ${call.ended_at}`}
        <br />
        Per-attempt timeline available via Compliance → Audit log (filter by message_id).
      </div>
    </div>
  );
}
